/* ============================================================================
   PAA — Cart drawer (finding cart-1) — Born to Rally / Wokiee 2.2.2
   Vanilla JS (no jQuery dependency; uses Shopify.formatMoney when present).

   Loaded `defer` from snippets/paa-cart-drawer.liquid (every page but /cart) and from
   sections/cart-template.liquid (/cart — only the free-shipping bar API is used there).
   Because the snippet sits before snippets/include_js.liquid in the document, this file
   executes BEFORE vendor.min.js / theme.js. Everything that touches Shopify.* therefore
   runs from DOMContentLoaded (all deferred scripts have executed by then) and again on
   `load` as a fallback.

   Triggers
     - Wokiee ajax add (.addtocart-js / .addtocart-item-js): wraps Shopify.onItemAdded,
       Shopify.onCartUpdate and Shopify.onError (vendor.min.js overrides) — render + open.
     - Third-party adds (Selleasy / Globo): fetch + XMLHttpRequest interceptors on
       /cart/add|change|update|clear → open (adds) + debounced refresh from /cart.js.
     - Selleasy's `lbSECartUpdated` event → free re-render from the cart it already fetched.
     - Header cart icon (.tt-cart .tt-dropdown-toggle): capture-phase click handler opens
       the drawer and stops Wokiee's dropdown handler (theme.js ttHeaderDropdown).

   Public API: window.PAACart = { open, close, refresh, render, shipBar, formatMoney }
   ========================================================================== */
(function () {
  'use strict';

  if (window.PAACart && window.PAACart.__loaded) { return; }

  var doc = document;
  var CART_MUTATION_RE = /\/cart\/(add|change|update|clear)(\.js|\.json)?$/;
  var ADD_RE = /\/cart\/add(\.js|\.json)?$/;
  var ADD_SIGNAL_TTL = 8000;   // ms: onCartUpdate only auto-opens if an add was seen this recently
  var QTY_DEBOUNCE = 350;      // ms: typed quantity
  var RECO_FALLBACK_DELAY = 1500;

  var cfg = {
    moneyFormat: '',   // from #paa-cd-config; else window.money_format (layout/theme.liquid) else ${{amount}}
    cartUrl: '/cart',
    rootUrl: '',       // from #paa-cd-config; else Shopify.routes.root else '/'
    reco: 'fallback',
    recoLimit: 4,
    placeholder: '',
    strings: {}
  };
  var S = cfg.strings;

  var el = {};
  var state = {
    initialised: false,
    cart: null,
    open: false,
    ownCall: false,
    addSignalAt: 0,
    lastAdded: null,
    lastRenderAt: 0,
    opener: null,
    refreshTimer: null,
    recoTimer: null,
    recoKey: '',
    recoCache: {},
    qtyTimers: {},
    toastTimer: null,
    liveTimer: null
  };

  /* ---------------------------------------------------------------- utils */
  function qs(sel, ctx) { return (ctx || doc).querySelector(sel); }
  function qsa(sel, ctx) { return Array.prototype.slice.call((ctx || doc).querySelectorAll(sel)); }

  function fill(str, map) {
    return String(str == null ? '' : str).replace(/\[(\w+)\]/g, function (m, k) {
      return Object.prototype.hasOwnProperty.call(map, k) ? String(map[k]) : m;
    });
  }

  function stripTags(s) { return String(s == null ? '' : s).replace(/<[^>]*>/g, ''); }

  function formatMoney(cents) {
    var fmt = cfg.moneyFormat || window.money_format || '${{amount}}';
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      try { return stripTags(window.Shopify.formatMoney(cents, fmt)); } catch (e) { /* fall through */ }
    }
    var n = (Number(cents) || 0) / 100;
    var amount = n.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return stripTags(fmt.replace(/\{\{\s*amount[a-z_]*\s*\}\}/, amount));
  }

  function route(path) {
    var root = cfg.rootUrl || (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return String(root).replace(/\/$/, '') + path;
  }

  function pathnameOf(url) {
    try { return new URL(String(url), window.location.href).pathname; } catch (e) { return String(url || '').split(/[?#]/)[0]; }
  }

  function sizedImage(url, width) {
    if (!url) { return cfg.placeholder || ''; }
    url = String(url);
    if (/^\/\//.test(url)) { url = window.location.protocol + url; }
    return url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=' + width;
  }

  function dispatch(name, detail) {
    try { doc.dispatchEvent(new CustomEvent(name, { detail: detail || {} })); } catch (e) { /* ignore */ }
  }

  function readConfig() {
    var node = doc.getElementById('paa-cd-config');
    if (!node) { return; }
    try {
      var parsed = JSON.parse(node.textContent || '{}');
      Object.keys(parsed).forEach(function (k) {
        if (k === 'strings' && parsed.strings) {
          Object.keys(parsed.strings).forEach(function (sk) { S[sk] = parsed.strings[sk]; });
        } else {
          cfg[k] = parsed[k];
        }
      });
    } catch (e) { console.warn('[paa-cart-drawer] config parse failed', e); }
  }

  function str(key, fallback) { return S[key] != null && S[key] !== '' ? S[key] : fallback; }

  /* ------------------------------------------------- free-shipping bar */
  function shipBar(bar, totalCents) {
    if (!bar) { return; }
    var threshold = parseInt(bar.getAttribute('data-threshold'), 10) || 0;
    if (threshold <= 0) { bar.hidden = true; return; }
    var total = Math.max(0, parseInt(totalCents, 10) || 0);
    var remaining = Math.max(0, threshold - total);
    var now = Math.min(total, threshold);
    var pct = Math.min(100, Math.round((now / threshold) * 100));
    var unlocked = remaining === 0;
    var text = unlocked
      ? (bar.getAttribute('data-unlocked-text') || '')
      : fill(bar.getAttribute('data-remaining-text') || '', { amount: formatMoney(remaining) });
    var textEl = qs('[data-paa-ship-text]', bar);
    var fillEl = qs('[data-paa-ship-fill]', bar);
    if (textEl) { textEl.textContent = text; }
    if (fillEl) { fillEl.style.width = pct + '%'; }
    bar.classList.toggle('paa-ship--unlocked', unlocked);
    bar.setAttribute('aria-valuenow', String(now));
    bar.setAttribute('aria-valuetext', text);
    bar.hidden = false;
  }

  /* ---------------------------------------------------------- rendering */
  function setText(node, text) { if (node) { node.textContent = text == null ? '' : String(text); } }

  function renderItem(item) {
    if (!el.tpl || !el.tpl.content || !el.tpl.content.firstElementChild) { return null; }
    var node = el.tpl.content.firstElementChild.cloneNode(true);
    var title = item.product_title || item.title || '';

    node.setAttribute('data-key', item.key || '');
    node.setAttribute('data-product-id', item.product_id || '');
    node.setAttribute('data-variant-id', item.variant_id || item.id || '');
    node.setAttribute('data-qty', item.quantity);

    var img = qs('img', node);
    var imgUrl = (item.featured_image && item.featured_image.url) || item.image || '';
    if (img) { img.src = imgUrl ? sizedImage(imgUrl, 160) : (cfg.placeholder || ''); img.alt = ''; }

    qsa('[data-paa-url]', node).forEach(function (a) { a.setAttribute('href', item.url || cfg.cartUrl); });
    setText(qs('[data-paa-title]', node), title);

    var variant = qs('[data-paa-variant]', node);
    if (variant) {
      if (item.variant_title && item.variant_title !== 'Default Title') {
        variant.textContent = String(item.variant_title).replace(/ \/ /g, ', ');
        variant.hidden = false;
      } else { variant.hidden = true; }
    }

    var props = qs('[data-paa-props]', node);
    if (props) {
      var keys = Object.keys(item.properties || {}).filter(function (k) {
        var v = item.properties[k];
        return k.charAt(0) !== '_' && v != null && String(v) !== '';
      });
      keys.forEach(function (k) {
        var li = doc.createElement('li');
        var kEl = doc.createElement('span'); kEl.className = 'paa-cd__prop-k'; kEl.textContent = k + ':';
        var vEl = doc.createElement('span'); vEl.className = 'paa-cd__prop-v';
        var v = String(item.properties[k]);
        if (/^https?:\/\/\S+\/uploads\//.test(v)) {
          var a = doc.createElement('a'); a.href = v; a.target = '_blank'; a.rel = 'noopener';
          a.textContent = v.split('/').pop();
          vEl.appendChild(a);
        } else { vEl.textContent = v; }
        li.appendChild(kEl); li.appendChild(doc.createTextNode(' ')); li.appendChild(vEl);
        props.appendChild(li);
      });
      props.hidden = keys.length === 0;
    }

    var plan = qs('[data-paa-plan]', node);
    if (plan) {
      var planName = item.selling_plan_allocation && item.selling_plan_allocation.selling_plan && item.selling_plan_allocation.selling_plan.name;
      plan.textContent = planName || '';
      plan.hidden = !planName;
    }

    var discounts = qs('[data-paa-discounts]', node);
    if (discounts) {
      var allocs = item.line_level_discount_allocations || [];
      allocs.forEach(function (d) {
        var li = doc.createElement('li');
        var t = (d.discount_application && d.discount_application.title) || '';
        li.textContent = t + (d.amount ? ' (-' + formatMoney(d.amount) + ')' : '');
        discounts.appendChild(li);
      });
      discounts.hidden = allocs.length === 0;
    }

    var old = qs('[data-paa-price-old]', node);
    if (old) {
      var showOld = item.original_line_price != null && item.final_line_price != null && item.original_line_price > item.final_line_price;
      old.textContent = showOld ? formatMoney(item.original_line_price) : '';
      old.hidden = !showOld;
    }
    setText(qs('[data-paa-price-now]', node), formatMoney(item.final_line_price != null ? item.final_line_price : item.line_price));

    var input = qs('[data-paa-qty-input]', node);
    if (input) { input.value = item.quantity; input.setAttribute('aria-label', str('quantity', 'Quantity') + ', ' + title); }
    var minus = qs('[data-paa-qty-minus]', node);
    if (minus) { minus.setAttribute('aria-label', str('decrease', 'Decrease quantity') + ', ' + title); }
    var plus = qs('[data-paa-qty-plus]', node);
    if (plus) { plus.setAttribute('aria-label', str('increase', 'Increase quantity') + ', ' + title); }
    var remove = qs('[data-paa-remove]', node);
    if (remove) { remove.setAttribute('aria-label', str('remove', 'Remove') + ' ' + title); }
    return node;
  }

  function renderCartDiscounts(cart) {
    if (!el.cartDiscounts) { return; }
    el.cartDiscounts.innerHTML = '';
    var apps = cart.cart_level_discount_applications || [];
    apps.forEach(function (d) {
      var li = doc.createElement('li');
      li.textContent = (d.title || '') + (d.total_allocated_amount ? ' (-' + formatMoney(d.total_allocated_amount) + ')' : '');
      el.cartDiscounts.appendChild(li);
    });
    el.cartDiscounts.hidden = apps.length === 0;
  }

  function countText(n) {
    return fill(n === 1 ? str('count_one', '[count] item') : str('count_other', '[count] items'), { count: n });
  }

  function headerBadge(cart) {
    var handler = window.addToCartHandler;
    if (handler && typeof handler.updateGeneralInfo === 'function') {
      try { handler.updateGeneralInfo(cart); } catch (e) { /* ignore */ }
    } else {
      qsa('.tt-badge-cart').forEach(function (b) {
        b.textContent = cart.item_count;
        b.classList.toggle('hide', cart.item_count === 0);
      });
    }
    if (window.jQuery) { try { window.jQuery('body').trigger('refreshCurrency'); } catch (e) { /* ignore */ } }
  }

  function focusSnapshot() {
    var active = doc.activeElement;
    if (!active || !el.items || !el.items.contains(active)) { return null; }
    var li = active.closest('.paa-cd__item');
    var control = active.getAttribute('data-paa-qty-minus') != null ? '[data-paa-qty-minus]'
      : active.getAttribute('data-paa-qty-plus') != null ? '[data-paa-qty-plus]'
      : active.getAttribute('data-paa-qty-input') != null ? '[data-paa-qty-input]'
      : active.getAttribute('data-paa-remove') != null ? '[data-paa-remove]' : null;
    return li && control ? { key: li.getAttribute('data-key'), control: control } : null;
  }

  function focusRestore(snap) {
    if (!snap || !el.items) { return; }
    var li = el.items.querySelector('[data-key="' + snap.key.replace(/"/g, '\\"') + '"]');
    var target = li ? qs(snap.control, li) : null;
    if (!target) { target = el.items.querySelector('[data-paa-remove]') || el.close; }
    if (target && state.open) { try { target.focus({ preventScroll: true }); } catch (e) { target.focus(); } }
  }

  function render(cart) {
    if (!cart || typeof cart.item_count === 'undefined') { return; }
    state.cart = cart;
    state.lastRenderAt = Date.now();
    if (state.refreshTimer) { clearTimeout(state.refreshTimer); state.refreshTimer = null; }

    if (el.root) {
      var snap = focusSnapshot();
      var items = cart.items || [];
      el.root.classList.remove('paa-cd--loading');
      // only swap the list when <template> is supported; otherwise keep the server-rendered lines
      if (el.items && el.tpl && el.tpl.content && el.tpl.content.firstElementChild) {
        var frag = doc.createDocumentFragment();
        items.forEach(function (it) { var n = renderItem(it); if (n) { frag.appendChild(n); } });
        el.items.innerHTML = '';
        el.items.appendChild(frag);
      }
      el.root.classList.toggle('paa-cd--empty', items.length === 0);
      if (el.empty) { el.empty.hidden = items.length > 0; }
      if (el.foot) { el.foot.hidden = items.length === 0; }
      setText(el.subtotal, formatMoney(cart.total_price));
      setText(el.count, countText(cart.item_count));
      renderCartDiscounts(cart);
      shipBar(el.ship, cart.total_price);
      if (items.length === 0 && el.reco) { el.reco.hidden = true; }
      focusRestore(snap);
      if (state.open) { maybeReco(); }
    }
    headerBadge(cart);
    dispatch('paa:cart:render', { cart: cart });
  }

  /* ----------------------------------------------------------- requests */
  function ownFetch(url, init) {
    state.ownCall = true;
    try { return window.fetch(url, init); } finally { state.ownCall = false; }
  }

  function cartApi(path, body) {
    if (!window.fetch) { return Promise.reject(new Error(str('error_generic', 'Something went wrong.'))); }
    var init = {
      method: body ? 'POST' : 'GET',
      credentials: 'same-origin',
      headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
    };
    if (body) { init.headers['Content-Type'] = 'application/json'; init.body = JSON.stringify(body); }
    return ownFetch(path, init).then(function (res) {
      return res.text().then(function (txt) {
        var data = null;
        try { data = txt ? JSON.parse(txt) : null; } catch (e) { data = null; }
        if (!res.ok) {
          var err = new Error((data && (data.description || data.message)) || str('error_generic', 'Something went wrong.'));
          err.status = res.status; err.data = data;
          throw err;
        }
        return data;
      });
    });
  }

  function refresh() {
    if (state.refreshTimer) { clearTimeout(state.refreshTimer); state.refreshTimer = null; }
    return cartApi(route('/cart.js')).then(function (cart) { render(cart); return cart; }).catch(function (e) {
      if (el.root) { el.root.classList.remove('paa-cd--loading'); }
      console.warn('[paa-cart-drawer] refresh failed', e);
      return null;
    });
  }

  function scheduleRefresh(delay, afterAdd) {
    if (!el.root) { return; }
    if (state.refreshTimer) { clearTimeout(state.refreshTimer); }
    el.root.classList.add('paa-cd--loading');
    state.refreshTimer = setTimeout(function () {
      state.refreshTimer = null;
      refresh().then(function (cart) {
        if (cart && afterAdd) { live(fill(str('cart_live', 'Cart updated: [count]'), { count: countText(cart.item_count) })); }
      });
    }, delay);
  }

  function itemByKey(key) {
    var items = (state.cart && state.cart.items) || [];
    for (var i = 0; i < items.length; i++) { if (items[i].key === key) { return items[i]; } }
    return null;
  }

  function changeLine(key, quantity, li) {
    var item = itemByKey(key);
    var title = item ? (item.product_title || item.title || '') : '';
    quantity = Math.max(0, parseInt(quantity, 10) || 0);
    if (li) { li.classList.add('paa-cd__item--busy'); }
    return cartApi(route('/cart/change.js'), { id: key, quantity: quantity }).then(function (cart) {
      render(cart);
      live(quantity === 0
        ? fill(str('removed_live', '[title] removed from your cart'), { title: title })
        : fill(str('updated_live', 'Quantity updated: [title], [qty]'), { title: title, qty: quantity }));
      return cart;
    }).catch(function (e) {
      toast(e && e.message);
      return refresh();
    });
  }

  /* --------------------------------------------------------- open/close */
  function focusables() {
    if (!el.panel) { return []; }
    return qsa('a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), iframe, [tabindex]:not([tabindex="-1"])', el.panel)
      .filter(function (n) {
        if (n.hidden || n.getAttribute('aria-hidden') === 'true') { return false; }
        var rects = n.getClientRects();
        return rects && rects.length > 0;
      });
  }

  function onKeydown(e) {
    if (!state.open) { return; }
    if (e.key === 'Escape' || e.key === 'Esc') { e.preventDefault(); close(); return; }
    if (e.key !== 'Tab') { return; }
    var list = focusables();
    if (!list.length) { e.preventDefault(); el.panel.focus(); return; }
    var first = list[0], last = list[list.length - 1], active = doc.activeElement;
    var inside = el.panel.contains(active);
    if (e.shiftKey) {
      if (!inside || active === first || active === el.panel) { e.preventDefault(); last.focus(); }
    } else if (!inside || active === last) { e.preventDefault(); first.focus(); }
  }

  function resetWokieeDropdown() {
    qsa('.tt-dropdown-obj.tt-cart').forEach(function (n) { n.classList.remove('active', 'activetoview'); });
    doc.body.classList.remove('tt-popup-dropdown');
  }

  function open(opener) {
    if (!el.root) { return; }
    if (opener && opener.nodeType === 1) { state.opener = opener; }
    else if (!state.open) { state.opener = doc.activeElement && doc.activeElement !== doc.body ? doc.activeElement : null; }
    resetWokieeDropdown();
    if (state.open) { return; }
    state.open = true;
    el.root.setAttribute('aria-hidden', 'false');
    doc.documentElement.classList.add('paa-cd-open');
    var gutter = window.innerWidth - doc.documentElement.clientWidth; if (gutter > 0 && gutter < 40) { doc.documentElement.style.paddingRight = gutter + 'px'; }
    // two frames so the visibility flip lands before the transform transition starts
    window.requestAnimationFrame(function () { el.root.classList.add('paa-cd--open'); });
    doc.addEventListener('keydown', onKeydown, true);
    setTimeout(function () {
      if (!state.open) { return; }
      var target = el.close || el.panel;
      try { target.focus({ preventScroll: true }); } catch (e) { target.focus(); }
    }, 40);
    maybeReco();
    dispatch('paa:cart:open', { cart: state.cart });
  }

  function close() {
    if (!el.root || !state.open) { return; }
    state.open = false;
    state.addSignalAt = 0; state.lastAdded = null;   // a late onCartUpdate must not re-open a drawer the shopper just closed
    doc.documentElement.style.paddingRight = '';
    el.root.classList.remove('paa-cd--open', 'paa-cd--more-open');
    if (el.more) { el.more.setAttribute('aria-expanded', 'false'); }
    el.root.setAttribute('aria-hidden', 'true');
    doc.documentElement.classList.remove('paa-cd-open');
    doc.removeEventListener('keydown', onKeydown, true);
    if (state.recoTimer) { clearTimeout(state.recoTimer); state.recoTimer = null; }
    var opener = state.opener;
    state.opener = null;
    if (opener && doc.body.contains(opener) && typeof opener.focus === 'function') {
      try { opener.focus({ preventScroll: true }); } catch (e) { opener.focus(); }
    }
    dispatch('paa:cart:close', {});
  }

  /* ----------------------------------------------------- toast + live */
  function toast(message) {
    var msg = message || str('error_generic', 'Sorry, something went wrong. Please try again.');
    if (!el.toast) { window.alert(msg); return; }
    el.toast.textContent = msg;
    el.toast.hidden = false;
    window.requestAnimationFrame(function () { el.toast.classList.add('paa-cd__toast--show'); });
    if (state.toastTimer) { clearTimeout(state.toastTimer); }
    state.toastTimer = setTimeout(function () {
      el.toast.classList.remove('paa-cd__toast--show');
      setTimeout(function () { el.toast.hidden = true; }, 220);
    }, 5000);
  }

  function live(text) {
    if (!el.live) { return; }
    el.live.textContent = '';
    if (state.liveTimer) { clearTimeout(state.liveTimer); }
    state.liveTimer = setTimeout(function () { el.live.textContent = text; }, 50);
  }

  function parseError(responseText) {
    var data = null;
    try { data = JSON.parse(responseText); } catch (e) { data = null; }
    if (!data) { return str('error_generic', 'Sorry, something went wrong. Please try again.'); }
    if (data.description) { return String(data.description); }
    if (data.message) { return String(data.message); }
    var parts = [];
    Object.keys(data).forEach(function (k) {
      var v = data[k];
      (Array.isArray(v) ? v : [v]).forEach(function (m) { parts.push(k + ' ' + m); });
    });
    return parts.length ? parts.join('; ') : str('error_generic', 'Sorry, something went wrong. Please try again.');
  }

  /* ----------------------------------------- fallback recommendations */
  function maybeReco() {
    if (!el.reco || !el.recoList || cfg.reco === 'never') { return; }
    if (state.recoTimer) { clearTimeout(state.recoTimer); state.recoTimer = null; }
    var items = (state.cart && state.cart.items) || [];
    if (!items.length) { el.reco.hidden = true; return; }
    if (selleasyPresent()) { el.reco.hidden = true; return; }
    var delay = cfg.reco === 'always' ? 0 : RECO_FALLBACK_DELAY;
    state.recoTimer = setTimeout(function () {
      state.recoTimer = null;
      if (!state.open) { return; }
      if (cfg.reco !== 'always' && selleasyPresent()) { el.reco.hidden = true; return; }
      loadReco();
    }, delay);
  }

  function selleasyPresent() {
    return !!(el.upsell && el.upsell.querySelector('lb-upsell-widget'));
  }

  function loadReco() {
    var items = (state.cart && state.cart.items) || [];
    if (!items.length) { return; }
    var productId = items[0].product_id;
    var inCart = {};
    items.forEach(function (it) { inCart[it.product_id] = true; });
    var key = productId + '|' + Object.keys(inCart).sort().join(',');
    if (state.recoKey === key && !el.reco.hidden) { return; }
    var cached = state.recoCache[productId];
    var p = cached ? Promise.resolve(cached) : (window.fetch
      ? window.fetch(route('/recommendations/products.json?product_id=' + encodeURIComponent(productId) + '&limit=' + (cfg.recoLimit * 2) + '&intent=related'), { credentials: 'same-origin', headers: { 'Accept': 'application/json' } })
        .then(function (r) { return r.ok ? r.json() : { products: [] }; })
        .then(function (d) { state.recoCache[productId] = d; return d; })
      : Promise.resolve({ products: [] }));
    p.then(function (data) {
      var products = ((data && data.products) || []).filter(function (prod) {
        if (!prod || !prod.available || inCart[prod.id]) { return false; }
        var tags = Array.isArray(prod.tags) ? prod.tags : String(prod.tags || '').split(',');
        return !tags.some(function (t) { return String(t).trim().toLowerCase() === 'globo-product-options'; });
      }).slice(0, cfg.recoLimit);
      state.recoKey = key;
      renderReco(products);
    }).catch(function () { /* recommendations are optional */ });
  }

  function renderReco(products) {
    if (!el.reco || !el.recoList) { return; }
    el.recoList.innerHTML = '';
    if (!products.length) { el.reco.hidden = true; return; }
    products.forEach(function (prod) {
      var li = doc.createElement('li'); li.className = 'paa-cd__reco-item';
      li.setAttribute('data-product-id', prod.id);

      var imgLink = doc.createElement('a'); imgLink.className = 'paa-cd__reco-img'; imgLink.href = prod.url || '#'; imgLink.tabIndex = -1; imgLink.setAttribute('aria-hidden', 'true');
      var img = doc.createElement('img'); img.alt = ''; img.loading = 'lazy'; img.width = 160; img.height = 160;
      var src = prod.featured_image || (prod.images && prod.images[0]) || '';
      img.src = src ? sizedImage(src, 320) : (cfg.placeholder || '');
      imgLink.appendChild(img);

      var name = doc.createElement('a'); name.className = 'paa-cd__reco-name'; name.href = prod.url || '#'; name.textContent = prod.title || '';

      var price = doc.createElement('span'); price.className = 'paa-cd__reco-price'; price.textContent = formatMoney(prod.price);
      if (prod.compare_at_price && prod.compare_at_price > prod.price) {
        var s = doc.createElement('s'); s.textContent = formatMoney(prod.compare_at_price); price.appendChild(s);
      }

      var action;
      var variants = prod.variants || [];
      if (variants.length === 1 && variants[0].available !== false) {
        action = doc.createElement('button'); action.type = 'button'; action.className = 'paa-cd__reco-add';
        action.setAttribute('data-paa-reco-add', variants[0].id);
        action.textContent = str('add', 'Add');
        action.setAttribute('aria-label', str('add', 'Add') + ' ' + (prod.title || ''));
      } else {
        action = doc.createElement('a'); action.className = 'paa-cd__reco-add'; action.href = prod.url || '#';
        action.textContent = str('choose_options', 'Choose options');
        action.setAttribute('aria-label', str('choose_options', 'Choose options') + ' – ' + (prod.title || ''));
      }

      li.appendChild(imgLink); li.appendChild(name); li.appendChild(price); li.appendChild(action);
      el.recoList.appendChild(li);
    });
    el.reco.hidden = false;
  }

  function addVariant(variantId, button) {
    if (button) { button.disabled = true; }
    return cartApi(route('/cart/add.js'), { items: [{ id: parseInt(variantId, 10), quantity: 1 }] }).then(function (data) {
      state.addSignalAt = Date.now();
      var added = data && data.items && data.items[0];
      return refresh().then(function () {
        live(fill(str('added_live', 'Added to cart: [title]'), { title: added ? (added.product_title || added.title || '') : '' }));
      });
    }).catch(function (e) {
      toast(e && e.message);
    }).then(function () { if (button && doc.body.contains(button)) { button.disabled = false; } });
  }

  /* ------------------------------------------------------------- hooks */
  function hookShopify() {
    var Sh = window.Shopify;
    if (!Sh || Sh.__paaCartDrawerHooked) { return; }
    // vendor.min.js must have installed Wokiee's overrides first (onItemAdded calls Shopify.getCart)
    if (typeof Sh.onItemAdded !== 'function' || typeof Sh.onCartUpdate !== 'function') { return; }
    Sh.__paaCartDrawerHooked = true;

    var origAdded = Sh.onItemAdded;
    Sh.onItemAdded = function (lineItem) {
      state.lastAdded = lineItem || null;
      state.addSignalAt = Date.now();
      try { return origAdded.apply(this, arguments); } catch (e) { console.warn('[paa-cart-drawer] onItemAdded original threw', e); }
    };

    var origUpdate = Sh.onCartUpdate;
    Sh.onCartUpdate = function (cart) {
      try { origUpdate.apply(this, arguments); } catch (e) { console.warn('[paa-cart-drawer] onCartUpdate original threw', e); }
      if (!cart || typeof cart.item_count === 'undefined') { return; }
      render(cart);
      if (Date.now() - state.addSignalAt < ADD_SIGNAL_TTL) {
        open();
        var added = state.lastAdded;
        state.lastAdded = null;
        live(added
          ? fill(str('added_live', 'Added to cart: [title]'), { title: added.product_title || added.title || '' })
          : fill(str('cart_live', 'Cart updated: [count]'), { count: countText(cart.item_count) }));
      }
    };

    var origError = Sh.onError;
    Sh.onError = function (xhr, textStatus) {
      var quickViewOpen = typeof window.modal_qv_open !== 'undefined' && window.modal_qv_open;
      if (doc.getElementById('modalAddToCartError') || quickViewOpen) {
        if (typeof origError === 'function') { return origError.apply(this, arguments); }
        return undefined;
      }
      toast(parseError(xhr && xhr.responseText));
      if (state.cart && state.cart.item_count > 0) { open(); }
      return undefined;
    };
  }

  function isCartMutation(url) { return CART_MUTATION_RE.test(pathnameOf(url)); }

  function onCartMutation(url, ok) {
    if (!ok || !el.root) { return; }
    var isAdd = ADD_RE.test(pathnameOf(url));
    if (isAdd) { state.addSignalAt = Date.now(); open(); }
    // Wokiee's own add path renders via the onCartUpdate wrapper (which cancels this timer);
    // third-party adds/changes land here and get one debounced /cart.js refresh.
    scheduleRefresh(isAdd ? 600 : 250, isAdd);
  }

  function installInterceptors() {
    if (window.__paaCartInterceptors) { return; }
    window.__paaCartInterceptors = true;

    if (window.fetch) {
      var origFetch = window.fetch;
      window.fetch = function (input, init) {
        var own = state.ownCall;
        var url = typeof input === 'string' ? input : (input && input.url) || '';
        var p = origFetch.apply(this, arguments);
        if (!own && url && isCartMutation(url)) {
          p.then(function (res) { onCartMutation(url, !!(res && res.ok)); }, function () { /* network error: nothing to do */ });
        }
        return p;
      };
    }

    if (window.XMLHttpRequest && window.XMLHttpRequest.prototype) {
      var proto = window.XMLHttpRequest.prototype;
      var origOpen = proto.open, origSend = proto.send;
      proto.open = function (method, url) {
        this.__paaUrl = url == null ? '' : String(url);
        return origOpen.apply(this, arguments);
      };
      proto.send = function () {
        var xhr = this, own = state.ownCall;
        if (!own && xhr.__paaUrl && isCartMutation(xhr.__paaUrl)) {
          xhr.addEventListener('loadend', function () {
            onCartMutation(xhr.__paaUrl, xhr.status >= 200 && xhr.status < 300);
          });
        }
        return origSend.apply(this, arguments);
      };
    }
  }

  function listenSelleasy() {
    window.addEventListener('lbSECartUpdated', function (e) {
      var cart = e && e.detail && e.detail.cart;
      if (cart && typeof cart.item_count !== 'undefined') { render(cart); }
    });
    if (el.upsell && window.MutationObserver) {
      new MutationObserver(function () {
        if (cfg.reco !== 'always' && selleasyPresent() && el.reco) { el.reco.hidden = true; }
      }).observe(el.upsell, { childList: true, subtree: true });
    }
  }

  function installHeaderCapture() {
    doc.addEventListener('click', function (e) {
      var target = e.target && e.target.nodeType === 1 ? e.target : (e.target && e.target.parentElement);
      if (!target || typeof target.closest !== 'function') { return; }
      var toggle = target.closest('.tt-cart .tt-dropdown-toggle');
      if (!toggle) { return; }
      e.preventDefault();
      e.stopImmediatePropagation();
      open(toggle);
    }, true);
  }

  /* --------------------------------------------------- drawer events */
  function bindDrawer() {
    qsa('[data-paa-close]', el.root).forEach(function (n) {
      n.addEventListener('click', function (e) { e.preventDefault(); close(); });
    });

    if (el.more) {
      el.more.addEventListener('click', function () {
        var openNow = el.root.classList.toggle('paa-cd--more-open');
        el.more.setAttribute('aria-expanded', openNow ? 'true' : 'false');
      });
    }

    if (el.items) {
      el.items.addEventListener('click', function (e) {
        var btn = e.target.closest('[data-paa-qty-minus], [data-paa-qty-plus], [data-paa-remove]');
        if (!btn || !el.items.contains(btn)) { return; }
        var li = btn.closest('.paa-cd__item');
        if (!li) { return; }
        var key = li.getAttribute('data-key');
        var qty = parseInt(li.getAttribute('data-qty'), 10) || 0;
        e.preventDefault();
        if (btn.hasAttribute('data-paa-remove')) { changeLine(key, 0, li); }
        else if (btn.hasAttribute('data-paa-qty-plus')) { changeLine(key, qty + 1, li); }
        else { changeLine(key, Math.max(0, qty - 1), li); }
      });

      var commitInput = function (input, immediate) {
        var li = input.closest('.paa-cd__item');
        if (!li) { return; }
        var key = li.getAttribute('data-key');
        var current = parseInt(li.getAttribute('data-qty'), 10) || 0;
        var wanted = parseInt(input.value, 10);
        if (state.qtyTimers[key]) { clearTimeout(state.qtyTimers[key]); delete state.qtyTimers[key]; }
        if (isNaN(wanted) || wanted < 0) { if (immediate) { input.value = current; } return; }
        if (wanted === current) { return; }
        var run = function () { delete state.qtyTimers[key]; changeLine(key, wanted, li); };
        if (immediate) { run(); } else { state.qtyTimers[key] = setTimeout(run, QTY_DEBOUNCE); }
      };
      el.items.addEventListener('input', function (e) {
        if (e.target && e.target.hasAttribute('data-paa-qty-input')) { commitInput(e.target, false); }
      });
      el.items.addEventListener('change', function (e) {
        if (e.target && e.target.hasAttribute('data-paa-qty-input')) { commitInput(e.target, true); }
      });
      el.items.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' && e.target && e.target.hasAttribute('data-paa-qty-input')) { e.preventDefault(); commitInput(e.target, true); }
      });
    }

    if (el.recoList) {
      el.recoList.addEventListener('click', function (e) {
        var btn = e.target.closest('[data-paa-reco-add]');
        if (!btn) { return; }
        e.preventDefault();
        addVariant(btn.getAttribute('data-paa-reco-add'), btn);
      });
    }

    // back/forward cache: the cart may have changed in checkout
    window.addEventListener('pageshow', function (e) { if (e.persisted) { refresh(); } });
  }

  /* -------------------------------------------------------------- init */
  /* Globo Product Options insurance (finding cart-1 QA, 2026-09-05): Globo only strips Wokiee's
     ajax class from its cloned Add-to-cart button when it recognises the theme (server-side scan per
     theme id: GPOConfigs.themeInfo.theme_name). On an unscanned theme id the clone keeps .addtocart-js,
     so Wokiee's ajax handler fires BEFORE Globo validates required fields and empty personalisation gets
     into the cart. Emulate the recognised behaviour: drop the class and any jQuery click handlers from the
     clone; Globo then validates and triggers the original (hidden) button, which Wokiee handles. No-op on
     themes Globo already recognises (the clone has no .addtocart-js there). */
  function guardGloboClone(root) {
    var clones = qsa('.gpo-clone-atc-button.addtocart-js, .gpo-clone-stickyAtc-button.addtocart-js', root || doc);
    clones.forEach(function (btn) {
      btn.classList.remove('addtocart-js', 'addtocart-item-js');
      try { if (window.jQuery) { window.jQuery(btn).off('click'); } } catch (e) {}
      btn.setAttribute('data-paa-globo-guard', '1');
    });
    return clones.length;
  }
  function installGloboGuard() {
    guardGloboClone();
    if (!window.MutationObserver) { return; }
    var mo = new MutationObserver(function (muts) {
      for (var i = 0; i < muts.length; i++) {
        var m = muts[i];
        if (m.type === 'childList' && m.addedNodes.length) { guardGloboClone(); return; }
        if (m.type === 'attributes' && m.target && m.target.classList && m.target.classList.contains('gpo-clone-atc-button') && m.target.classList.contains('addtocart-js')) { guardGloboClone(); return; }
      }
    });
    mo.observe(doc.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
  }

  function init() {
    if (state.initialised) { hookShopify(); return; }
    state.initialised = true;
    if (doc.querySelector('form[action*="/cart/add"]')) { try { installGloboGuard(); } catch (e) { console.warn('[paa-cart-drawer] globo guard failed', e); } }
    readConfig();

    el.root = doc.getElementById('paa-cart-drawer');
    if (el.root) {
      el.panel = qs('[data-paa-panel]', el.root);
      el.body = qs('[data-paa-body]', el.root);
      el.items = qs('[data-paa-items]', el.root);
      el.empty = qs('[data-paa-empty]', el.root);
      el.foot = qs('[data-paa-foot]', el.root);
      el.subtotal = qs('[data-paa-subtotal]', el.root);
      el.count = qs('[data-paa-count]', el.root);
      el.ship = qs('[data-paa-ship]', el.root);
      el.close = qs('.paa-cd__close', el.root);
      el.upsell = doc.getElementById('paa-drawer-upsell');
      el.reco = qs('[data-paa-reco]', el.root);
      el.recoList = qs('[data-paa-reco-list]', el.root);
      el.cartDiscounts = qs('[data-paa-cart-discounts]', el.root);
      el.more = qs('[data-paa-more]', el.root);
      el.tpl = doc.getElementById('paa-cd-item-tpl');
      el.toast = qs('[data-paa-toast]');
      el.live = qs('[data-paa-live]');

      bindDrawer();
      installHeaderCapture();
      installInterceptors();
      listenSelleasy();
    }
    hookShopify();
  }

  window.PAACart = {
    __loaded: true,
    open: open,
    close: close,
    refresh: refresh,
    render: render,
    shipBar: shipBar,
    formatMoney: formatMoney,
    isOpen: function () { return state.open; },
    getCart: function () { return state.cart; }
  };

  // Deferred scripts run before DOMContentLoaded in document order; this file sits before
  // vendor.min.js, so wait for DOMContentLoaded (or load, if that already fired).
  if (doc.readyState === 'complete') {
    init();
  } else {
    doc.addEventListener('DOMContentLoaded', init);
    window.addEventListener('load', init);
  }
})();
